import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { Ref, ComputedRef } from "vue";
import OAuthToken from "@/types/OAuthToken";
import User from "@/types/User";
import getAccessToken from "@/api/requests/getAccessToken";
import getUserData from "@/api/requests/getUserData";
import { useRegisterForm } from "@/composables/useRegisterForm";

export const useUserStore = defineStore("user", () => {
  // State
  const data: Ref<User> = ref({} as User);
  const logged: Ref<boolean> = ref(false);
  const token: Ref<OAuthToken | null> = ref(null);

  const registerForm = useRegisterForm();

  // Getters
  const id: ComputedRef<string> = computed(() => data.value.id);

  // Actions
  function setUser(user: User): void {
    data.value = { ...user };
    logged.value = true;
  }

  function setToken(newToken: OAuthToken): void {
    token.value = newToken;
  }

  async function register(): Promise<User> {
    const result = await registerForm.submit();

    await login(result.email, (registerForm.model.value).password);

    return result;
  }

  async function login(email: string, password: string) {
    const tokenResponse = await getAccessToken(email, password);
    token.value = tokenResponse.data;

    const userResponse = await getUserData();
    data.value = userResponse.data;
  }

  function logout(): void {
    data.value = {} as User;
    logged.value = false;
  }

  return {
    // State
    data,
    logged,
    token,
    form: registerForm,
    // Getters
    id,
    // Actions
    setUser,
    setToken,
    register,
    login,
    logout,
  };
}, {
  persist: true,
});
